/**
 * Normalizes third-party tracking pixels for accessibility (WCAG 1.1.1).
 *
 * Vendors like Google Floodlight / DoubleClick inject 1×1 conversion pixels
 * (e.g. when add-to-cart fires) as `<img>` elements without an `alt` attribute,
 * which trips the "tracking/spacer image must have empty alternative text" rule.
 * We don't own that markup, so we watch for these pixels and set `alt=""` so
 * assistive tech skips them.
 */

/** Hostname signature of tracking pixels we want to silence for AT. */
const TRACKING_PIXEL_SRC = /doubleclick\.net/i;

/**
 * Give a tracking-pixel `<img>` empty alt text so assistive tech ignores it.
 * No-op for any image that isn't a recognized tracking pixel.
 * @param {HTMLImageElement} img - candidate image element
 */
const normalizeTrackingPixel = (img) => {
  if (!TRACKING_PIXEL_SRC.test(img.src)) return;
  if (img.getAttribute('alt') !== '') img.setAttribute('alt', '');
};

/**
 * Inspect a mutation-added node (and any `<img>` descendants) for tracking
 * pixels that need their alt text normalized.
 * @param {Node} node - a node added to the DOM
 */
const scanNode = (node) => {
  if (!(node instanceof HTMLElement)) return;
  if (node instanceof HTMLImageElement) normalizeTrackingPixel(node);
  node.querySelectorAll('img').forEach(normalizeTrackingPixel);
};

let initialized = false;

/**
 * Sweep tracking pixels already in the DOM, then observe for ones injected
 * later (Floodlight fires its add-to-cart pixel after user interaction). The
 * observer is created only once even though init runs on every section load.
 */
export const initTrackingPixelAlt = () => {
  document.querySelectorAll('img').forEach(normalizeTrackingPixel);

  if (initialized) return;
  initialized = true;

  const observer = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach(scanNode));
  });

  observer.observe(document.body, { childList: true, subtree: true });
};
